import { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import { useDispatch, useSelector, useStore } from 'react-redux';

import { LocalCacheHandler } from '~/utils/local-cache-handler';
import { CACHE_ACTIONS } from '~/constants/cache-actions.constants';
import { loadFromCacheAction } from '~/store/actions/load-from-cache';
import usePersistedFilters from '~/_shared/hooks/usePersistedFilters';

import * as boardActions from './actions';
import { BOARD_ID_MAP } from './constants';
import {
  MARKETING_FILTER_KEYS,
  getDefaultMarketingFilters,
  toBoardDate,
  toStoredMonth,
  getMonthYear,
  shouldUseMarketingCache,
  getMarketingCachePathname,
} from './filters';

/**
 * useMarketingBoard — loads one marketing board (missoes / convites) for the
 * month kept in its persisted filters (survives a refresh, per board).
 *
 * - Mount loads the restored month; changing the month reloads.
 * - With `useCache`, the IndexedDB cache is used only for the current month
 *   and only this board's entry, so another board's/month's cards never flash.
 * - redux `marketing.selectedDate` follows the loaded month (BOARD_LOAD.SUCCESS).
 */
var useMarketingBoard = function(boardType, options) {
  var opts = options || {};
  var dispatch = useDispatch();
  var store = useStore();
  var needsReload = useSelector(function(state) { return state.marketing.needsReload; });
  var isMountedRef = useRef(true);
  var isFirstLoadRef = useRef(true);
  var latestRequestRef = useRef(0);

  var persisted = usePersistedFilters(MARKETING_FILTER_KEYS[boardType], getDefaultMarketingFilters);
  var month = persisted.filters.month;
  var setFilters = persisted.setFilters;

  var [isLoading, setIsLoading] = useState(true);

  // Board components and Header work with 'MM-YYYY'.
  var date = toBoardDate(month);
  var setDate = useCallback(function(newDate) {
    setFilters({ month: toStoredMonth(newDate) });
  }, [setFilters]);

  var loadData = useCallback(async function(showLoadingState, readCache) {
    if (!isMountedRef.current) return;
    var requestId = latestRequestRef.current + 1;
    latestRequestRef.current = requestId;
    var isLatest = function() { return isMountedRef.current && requestId === latestRequestRef.current; };

    if (showLoadingState) {
      setIsLoading(true);
    }

    var boardId = BOARD_ID_MAP[boardType];
    var cachePathname = getMarketingCachePathname(boardType);
    var usesCache = !!opts.useCache && shouldUseMarketingCache(month);

    try {
      if (readCache && usesCache) {
        // Without `dispatch`, LocalCacheHandler.load just returns the entry.
        var cached = await LocalCacheHandler.load({ pathname: cachePathname });
        if (!isLatest()) return;
        if (cached && cached.board) {
          var current = store.getState().marketing;
          var boardsData = Object.assign({}, current.data);
          boardsData[boardId] = cached.board;
          dispatch(loadFromCacheAction({
            actionName: CACHE_ACTIONS.marketing,
            cachedData: Object.assign({}, current, { data: boardsData }),
          }));
        }
      }

      var period = getMonthYear(month);
      await dispatch(boardActions.load(boardType, period.month, period.year, false));

      if (usesCache && isLatest()) {
        var loaded = store.getState().marketing;
        LocalCacheHandler.save({
          pathname: cachePathname,
          data: { board: loaded.data && loaded.data[boardId] },
        });
      }
    } catch (error) {
      console.log(error);
    } finally {
      if (isLatest()) {
        setIsLoading(false);
      }
    }
  }, [dispatch, store, boardType, month, opts.useCache]);

  // Set board type once.
  useEffect(function() {
    dispatch(boardActions.setBoardType(boardType));
  }, [dispatch, boardType]);

  // Mount (restored month) and every month change.
  useEffect(function() {
    var readCache = isFirstLoadRef.current;
    isFirstLoadRef.current = false;
    loadData(true, readCache);
  }, [month]);

  // Reload data when needsReload flag is set (after CRUD operations)
  useEffect(function() {
    if (needsReload) {
      loadData(false, false); // Don't show loading spinner for CRUD reloads
    }
  }, [needsReload, loadData]);

  useEffect(function() {
    return function() {
      isMountedRef.current = false;
    };
  }, []);

  var filtersContext = useMemo(function() {
    return { persisted: persisted };
  }, [persisted.filters, persisted.wasRestored, persisted.isDefault]);

  return {
    isLoading: isLoading,
    setIsLoading: setIsLoading,
    date: date,
    setDate: setDate,
    filtersContext: filtersContext,
  };
};

export default useMarketingBoard;
